import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProfile } from '../../lib/api';
import { useAuthStore } from '../../lib/authStore';
import { SetupWizard } from './SetupWizard';

// ─── OnboardingGate ───────────────────────────────────────────────────────────
// Wraps the authenticated app tree. On a user's first visit (no completed
// dashboard onboarding) it overlays the full-screen SetupWizard ON TOP of the app
// — the app still renders behind it, so nothing is unmounted and the wizard can
// hand off cleanly to the live dashboard on finish.
//
// FIRST-RUN SIGNAL: `profile.dashboard.onboardedAt === undefined` ⇒ show the
// wizard. An un-onboarded user has no saved layout, so the server returns an empty
// normalized layout (no widgets, no onboardedAt) — which reads as "not onboarded".
//
// FAIL-OPEN (never wedge a user): if the profile query errors, is still loading,
// or auth is disabled (local/CI), we render the app normally and DO NOT show the
// wizard. The wizard is an enhancement, never a gate that can lock someone out.

export function OnboardingGate({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const authed = status === 'authenticated';

  const { data: profile, isError } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: authed,
    staleTime: 60_000,
  });

  // Fail-open: only consider showing the wizard for an authenticated user whose
  // profile loaded cleanly. Auth disabled / loading / error ⇒ app as normal.
  const onboarded = profile?.dashboard?.onboardedAt !== undefined;
  // DEV-only preview hook: append ?forceWizard to the URL to render the wizard
  // regardless of auth/profile, for local UX/QA. Guarded by import.meta.env.DEV so
  // it has zero effect in production/INT builds.
  const forcePreview =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('forceWizard');
  const showWizard = forcePreview || (authed && !isError && !!profile && !onboarded);

  return (
    <>
      {children}
      {showWizard && <SetupWizard />}
    </>
  );
}
